import { Autocomplete, TextField, createFilterOptions } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { centroCusto, centroCusto_v2 } from 'service/api'
import { useStore } from 'store'
import { mapRenderCentros } from 'templates/Cadastro/CentroCusto/mapArvore'
import { SelectProps } from './types'

interface IProps extends SelectProps {
    /** Não filtrar por empresa e sim por cliente */
    listAll?: boolean
}

type CentroCustoOption = {
    id: number
    codigo: string
    nomeCentroCusto: string
    space: number
}

const filterOptions = createFilterOptions<CentroCustoOption>({
    stringify: option => `${option.codigo} ${option.nomeCentroCusto}`,
})

export const SelectCentroCusto: React.FC<IProps> = ({
    size = 'small',
    variant = 'filled',
    width = 250,
    children,
    listAll = false,
}) => {
    const empresaId = useStore(state => state.companyId)
    const costCenterId = useStore(state => state.costCenterId)
    const setCostCenterId = useStore(state => state.setCostCenterId)

    const { data: filteredData = [], isFetched: isFilteredFetched } =
        centroCusto.useFetch({
            queryParams: { empresaId },
            enabled: !!empresaId && !listAll,
        })
    const { data: allData = [], isFetched: isAllFetched } =
        centroCusto_v2.useFetch({ enabled: listAll })

    const data = listAll ? allData : filteredData,
        isFetched = listAll ? isAllFetched : isFilteredFetched

    const options = useMemo<CentroCustoOption[]>(() => {
        const centros = mapRenderCentros(data).map(node => ({
            id: node.id,
            codigo: node.codigo,
            nomeCentroCusto: node.nomeCentroCusto,
            space: node.space,
        }))

        return [
            {
                id: -1,
                codigo: '',
                nomeCentroCusto: 'Não filtrar',
                space: 0,
            },
            ...centros,
        ]
    }, [data])

    useEffect(() => {
        if (
            costCenterId > 0 &&
            isFetched &&
            !data.map(it => it.id).includes(costCenterId)
        )
            setCostCenterId(-1)
    }, [empresaId, isFetched])

    const selectedCentroCusto =
        options.find(it => it.id === costCenterId) ?? options[0] ?? null

    return (
        <Autocomplete
            style={{ width }}
            options={options}
            value={selectedCentroCusto}
            onChange={(_, value) => {
                setCostCenterId(value?.id ?? -1)
            }}
            filterOptions={filterOptions}
            getOptionLabel={option => {
                if (option.id === -1) return option.nomeCentroCusto
                return `${option.codigo} - ${option.nomeCentroCusto}`
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
                <li
                    {...props}
                    key={option.id}
                    title={option.nomeCentroCusto}
                    style={{
                        ...props.style,
                        paddingLeft: `${16 + option.space * 24}px`,
                    }}
                >
                    {option.id === -1
                        ? option.nomeCentroCusto
                        : `${option.codigo} - ${option.nomeCentroCusto}`}
                </li>
            )}
            renderInput={params => (
                <TextField
                    {...params}
                    name="centroCustoId"
                    label="Centro de custo"
                    size={size}
                    variant={variant}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    placeholder="Selecione um centro de custo"
                >
                    {children}
                </TextField>
            )}
            noOptionsText={
                <span className="text-muted-foreground select-none">
                    Não há centros de custo cadastrados
                </span>
            }
            slotProps={{
                paper: {
                    sx: {
                        maxHeight: '50vh',
                        maxWidth: width,
                    },
                },
            }}
            clearIcon={null}
        />
    )
}
